import type { ReactNode } from 'react';
import { Badge, Button, Card, Group, Text } from '@mantine/core';

/**
 * One setting, with where its value came from written on it.
 *
 * This is the whole design of these screens in one component. A publisher sets defaults once and
 * every brand takes what it does not answer, which is useful and invisible: without something
 * saying so, an editor cannot tell a colour they chose from a colour they were given, and the first
 * time a brand looks wrong they will change a field that was never the one deciding. So every
 * field says which of the two it is, names the level it came from when it is inherited, and offers
 * the one move that changes that.
 *
 * Going back to inherited is a button of its own rather than emptying the box. Emptying a box is
 * how you say "show nothing", which is a value a publisher may well want, and the two must not be
 * the same gesture.
 */
export function OverrideField({
  label,
  overridden,
  inheritedFrom,
  canEdit,
  onOverride,
  onInherit,
  children,
}: {
  /** The field's name, used in the buttons so each one reads on its own. */
  label: string;
  overridden: boolean;
  /** What the value falls back to, named: a publisher, or the quizee default. */
  inheritedFrom: string;
  canEdit: boolean;
  onOverride: () => void;
  onInherit: () => void;
  /** The input itself, which carries the visible label. */
  children: ReactNode;
}) {
  return (
    <Card withBorder radius="md" p="md" component="li">
      {children}
      <Group gap="sm" mt="sm" align="center" wrap="wrap">
        {overridden ? (
          <Badge size="sm" radius="sm" variant="light" color="violet" tt="none">
            Set here
          </Badge>
        ) : (
          <Badge size="sm" radius="sm" variant="light" color="gray" tt="none">
            Inherited from {inheritedFrom}
          </Badge>
        )}
        {canEdit &&
          (overridden ? (
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              px={0}
              onClick={onInherit}
              aria-label={`Go back to the inherited ${label.toLowerCase()}`}
            >
              Go back to inherited
            </Button>
          ) : (
            <Button
              variant="subtle"
              size="compact-sm"
              px={0}
              onClick={onOverride}
              aria-label={`Set your own ${label.toLowerCase()}`}
            >
              Set your own
            </Button>
          ))}
      </Group>
    </Card>
  );
}

/** The note that stands in for the controls an editor does not get. */
export function ReadOnlyNote({ what, owner }: { what: string; owner: string }) {
  return (
    <Text c="dimmed" size="sm" mt="sm">
      These are the {what} readers see. Only an owner can change them, so ask one of the owners of{' '}
      {owner} if something here needs to be different.
    </Text>
  );
}
